/**
 * AIReviewStepNavigator - Left sidebar step navigation for AI Review (MA-FE-073)
 */

import React from "react";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  MessageSquare,
  HelpCircle,
  TextCursorInput,
  Shield,
  Calendar,
  PhoneForwarded,
  GitBranch,
  Circle,
  Check,
  Edit3,
} from "lucide-react";

// Icon mapping for node types
const NODE_TYPE_ICONS = {
  message: MessageSquare,
  question: HelpCircle,
  input: TextCursorInput,
  consent: Shield,
  booking: Calendar,
  transfer: PhoneForwarded,
  condition: GitBranch,
};

// Color mapping for node types
const NODE_TYPE_COLORS = {
  message: "text-indigo-400",
  question: "text-indigo-400",
  response: "text-indigo-400",
  input: "text-purple-400",
  consent: "text-amber-400",
  booking: "text-blue-400",
  transfer: "text-cyan-400",
  condition: "text-orange-400",
};

export default function AIReviewStepNavigator({
  steps,
  currentStepIndex,
  approvedSteps,
  editedSteps,
  onSelectStep,
}) {
  return (
    <div className="w-64 bg-slate-900/50 border-r border-slate-700/50 flex flex-col">
      <div className="p-4 border-b border-slate-700/50">
        <h2 className="text-sm font-medium text-slate-200">Review Steps</h2>
        <p className="text-xs text-slate-400 mt-1">
          {approvedSteps.size} of {steps.length} approved
        </p>
      </div>
      
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {steps.map((step, index) => {
            const Icon = NODE_TYPE_ICONS[step.node_type] || Circle;
            const colorClass = NODE_TYPE_COLORS[step.node_type] || "text-slate-400";
            const isActive = index === currentStepIndex;
            const isApproved = approvedSteps.has(step.id);
            const isEdited = editedSteps.has(step.id);
            const label = step.metadata?.label || step.node_type || `Step ${index + 1}`;
            
            return (
              <button
                key={step.id}
                onClick={() => onSelectStep(index)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors",
                  isActive 
                    ? "bg-purple-500/20 border border-purple-500/30" 
                    : "hover:bg-slate-800/50 border border-transparent",
                )}
                data-testid={`step-nav-${index}`}
              >
                {/* Step Number */}
                <span className={cn(
                  "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium",
                  isApproved
                    ? "bg-emerald-500/20 text-emerald-400"
                    : isActive
                      ? "bg-purple-500/30 text-purple-300"
                      : "bg-slate-700/50 text-slate-400"
                )}>
                  {isApproved ? <Check className="h-3 w-3" /> : index + 1}
                </span>
                
                {/* Icon */}
                <Icon className={cn("h-4 w-4 flex-shrink-0", colorClass)} />
                
                {/* Label */}
                <div className="flex-1 min-w-0">
                  <span className={cn(
                    "text-sm truncate block",
                    isActive ? "text-slate-100" : "text-slate-300"
                  )}>
                    {label}
                  </span>
                  <span className="text-xs text-slate-500 capitalize">
                    {step.node_type}
                  </span>
                </div>
                
                {/* Status Badges */}
                <div className="flex-shrink-0 flex items-center gap-1">
                  {isEdited && (
                    <Badge variant="outline" className="text-[10px] px-1 py-0 border-blue-500/30 text-blue-400">
                      <Edit3 className="h-2.5 w-2.5" />
                    </Badge>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
